import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle, XCircle, Fish, Search, ExternalLink } from "lucide-react";
import { Input } from "@/components/ui/input";

interface Catch {
  id: string;
  fish_species: string;
  weight_lbs: number;
  date_caught: string;
  status: string;
  general_location: string;
  angler_id: string;
  photo_url: string | null;
  profiles: {
    angler_name: string;
  };
}

export const AdminCatches = () => {
  const navigate = useNavigate();
  const [catches, setCatches] = useState<Catch[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCatches, setSelectedCatches] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const { toast } = useToast();

  const fetchCatches = async () => {
    try {
      const { data, error } = await supabase
        .from("catches")
        .select(`
          *,
          profiles (
            angler_name
          )
        `)
        .order("date_submitted", { ascending: false });

      if (error) throw error;
      setCatches(data || []);
    } catch (error) {
      console.error("Error fetching catches:", error);
      toast({
        title: "Error",
        description: "Failed to load catches",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCatches();
  }, []);

  const updateCatchStatus = async (catchId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from("catches")
        .update({ 
          status: newStatus,
          verified_at: newStatus === 'verified' ? new Date().toISOString() : null
        })
        .eq("id", catchId);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Catch ${newStatus}`,
      });

      fetchCatches();
    } catch (error) {
      console.error("Error updating catch:", error);
      toast({
        title: "Error",
        description: "Failed to update catch status",
        variant: "destructive",
      });
    }
  };

  const bulkUpdateStatus = async (newStatus: string) => {
    if (selectedCatches.size === 0) {
      toast({
        title: "No catches selected",
        description: "Please select at least one catch",
        variant: "destructive",
      });
      return;
    }

    try {
      const { error } = await supabase
        .from("catches")
        .update({ 
          status: newStatus,
          verified_at: newStatus === 'verified' ? new Date().toISOString() : null
        })
        .in("id", Array.from(selectedCatches));

      if (error) throw error;

      toast({
        title: "Success",
        description: `${selectedCatches.size} catch(es) ${newStatus}`,
      });

      setSelectedCatches(new Set());
      fetchCatches();
    } catch (error) {
      console.error("Error bulk updating catches:", error);
      toast({
        title: "Error",
        description: "Failed to update catches",
        variant: "destructive",
      });
    }
  };

  const toggleCatchSelection = (catchId: string) => {
    setSelectedCatches(prev => {
      const newSet = new Set(prev);
      if (newSet.has(catchId)) {
        newSet.delete(catchId);
      } else {
        newSet.add(catchId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedCatches.size === pendingCatches.length) {
      setSelectedCatches(new Set());
    } else {
      setSelectedCatches(new Set(pendingCatches.map(c => c.id)));
    }
  };

  const pendingCatches = catches.filter(c => c.status === 'pending');
  
  const filteredCatches = catches.filter(c => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      c.fish_species.toLowerCase().includes(search) ||
      c.general_location.toLowerCase().includes(search) ||
      c.profiles?.angler_name?.toLowerCase().includes(search)
    );
  });

  if (loading) {
    return <div className="text-center py-8">Loading catches...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Fish className="h-5 w-5" />
          <h2 className="text-2xl font-bold">All Catches ({filteredCatches.length})</h2>
        </div>
        
        {pendingCatches.length > 0 && (
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={toggleSelectAll}
            >
              {selectedCatches.size === pendingCatches.length ? 'Deselect All' : 'Select All Pending'}
            </Button>
            <Button
              size="sm"
              onClick={() => bulkUpdateStatus('verified')}
              disabled={selectedCatches.size === 0}
              className="flex items-center gap-2"
            >
              <CheckCircle className="h-4 w-4" />
              Bulk Approve ({selectedCatches.size})
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => bulkUpdateStatus('rejected')}
              disabled={selectedCatches.size === 0}
              className="flex items-center gap-2"
            >
              <XCircle className="h-4 w-4" />
              Bulk Reject ({selectedCatches.size})
            </Button>
          </div>
        )}
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search by angler name, species, or location..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {catches.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No catches found
          </CardContent>
        </Card>
      ) : filteredCatches.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            No catches found matching "{searchTerm}"
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredCatches.map((catch_item) => (
            <Card 
              key={catch_item.id}
              className="hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => navigate(`/catch/${catch_item.id}`)}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    {catch_item.status === 'pending' && (
                      <Checkbox
                        checked={selectedCatches.has(catch_item.id)}
                        onCheckedChange={() => toggleCatchSelection(catch_item.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="mt-1"
                      />
                    )}
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        {catch_item.fish_species}
                      <Badge variant={
                        catch_item.status === 'verified' ? 'default' :
                        catch_item.status === 'rejected' ? 'destructive' :
                        'secondary'
                      }>
                        {catch_item.status}
                      </Badge>
                      <ExternalLink className="h-4 w-4 text-muted-foreground" />
                    </CardTitle>
                    <CardDescription>
                      By {catch_item.profiles?.angler_name || 'Unknown'} • {new Date(catch_item.date_caught).toLocaleDateString()}
                    </CardDescription>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold">{catch_item.weight_lbs} lbs</div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {catch_item.photo_url && (
                    <div className="rounded-lg overflow-hidden border">
                      <img 
                        src={catch_item.photo_url} 
                        alt={`${catch_item.fish_species} catch`}
                        className="w-full h-64 object-cover"
                      />
                    </div>
                  )}
                  <div className="text-sm text-muted-foreground">
                    <strong>Location:</strong> {catch_item.general_location}
                  </div>
                  
                  {catch_item.status === 'pending' && (
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          updateCatchStatus(catch_item.id, 'verified');
                        }}
                        className="flex items-center gap-2"
                      >
                        <CheckCircle className="h-4 w-4" />
                        Verify
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={(e) => {
                          e.stopPropagation();
                          updateCatchStatus(catch_item.id, 'rejected');
                        }}
                        className="flex items-center gap-2"
                      >
                        <XCircle className="h-4 w-4" />
                        Reject
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
